import { lazy, Suspense, memo, useMemo } from "react";
import {
  RocketLaunchIcon,
  ComputerDesktopIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import platformConnectionsLight from "../images/platform-connections-light.svg";

// Lazy load heavy TestimonialsBackground component
const TestimonialsBackground = lazy(() => import("./TestimonialsBackground"));

// Extract static values outside component to avoid recreation
const GRADIENT_CLIP_PATH =
  "polygon(63.1% 29.5%, 100% 17.1%, 76.6% 3%, 48.4% 0%, 44.6% 4.7%, 54.5% 25.3%, 59.8% 49%, 55.2% 57.8%, 44.4% 57.2%, 27.8% 47.9%, 35.1% 81.5%, 0% 97.7%, 39.2% 100%, 35.2% 81.4%, 97.2% 52.8%, 63.1% 29.5%)";

const HeroSection: React.FC = () => {
  const shouldReduceMotion = useReducedMotion();

  // Memoize animation variants to prevent recreation on each render
  const backgroundAnimation = useMemo(
    () =>
      shouldReduceMotion
        ? {}
        : {
            scale: [1, 1.1, 1],
            rotate: [0, 5, 0],
          },
    [shouldReduceMotion]
  );

  const backgroundTransition = useMemo(
    () =>
      shouldReduceMotion
        ? {}
        : {
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut" as const,
          },
    [shouldReduceMotion]
  );

  const gradientTextAnimation = useMemo(
    () =>
      shouldReduceMotion
        ? {}
        : {
            filter: ["brightness(1)", "brightness(1.2)", "brightness(1)"],
          },
    [shouldReduceMotion]
  );

  const gradientTextTransition = useMemo(
    () =>
      shouldReduceMotion
        ? {}
        : {
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut" as const,
          },
    [shouldReduceMotion]
  );

  const shimmerAnimation = useMemo(
    () =>
      shouldReduceMotion
        ? {}
        : {
            x: ["-100%", "100%"],
          },
    [shouldReduceMotion]
  );

  const shimmerTransition = useMemo(
    () =>
      shouldReduceMotion
        ? {}
        : {
            duration: 2,
            repeat: Infinity,
            repeatDelay: 1,
            ease: "easeInOut" as const,
          },
    [shouldReduceMotion]
  );

  const iconAnimation = useMemo(
    () =>
      shouldReduceMotion
        ? {}
        : {
            rotate: [0, 10, -10, 0],
          },
    [shouldReduceMotion]
  );

  const iconTransition = useMemo(
    () =>
      shouldReduceMotion
        ? {}
        : {
            duration: 2,
            repeat: Infinity,
            repeatDelay: 3,
          },
    [shouldReduceMotion]
  );

  return (
    <div className="relative isolate overflow-hidden h-screen flex items-center justify-center">
      <Suspense fallback={null}>
        <TestimonialsBackground />
      </Suspense>
      <motion.div
        aria-hidden="true"
        className="absolute left-1/2 right-0 top-0 -z-10 -ml-24 transform-gpu overflow-hidden blur-3xl lg:ml-24 xl:ml-48"
        style={{ willChange: shouldReduceMotion ? "auto" : "transform" }}
        animate={backgroundAnimation}
        transition={backgroundTransition}
      >
        <div
          style={{
            clipPath: GRADIENT_CLIP_PATH,
          }}
          className="aspect-[801/1036] w-[50.0625rem] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30"
        />
      </motion.div>
      <div className="overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 pb-32 pt-56 sm:pt-60 lg:px-8 lg:pt-32">
          <div className="mx-auto max-w-2xl gap-x-14 lg:mx-0 lg:flex lg:max-w-none lg:items-center">
            <motion.div
              className="relative w-full max-w-xl lg:shrink-0 xl:max-w-2xl text-center sm:text-left"
              initial={shouldReduceMotion ? false : { opacity: 0, x: -50 }}
              animate={shouldReduceMotion ? {} : { opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <motion.h1
                className="mt-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl"
                initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
                animate={shouldReduceMotion ? {} : { opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                Effortlessly{" "}
                <motion.span
                  className="bg-gradient-to-r from-softBlue via-brightPurple to-deepPurple bg-clip-text text-transparent"
                  animate={gradientTextAnimation}
                  transition={gradientTextTransition}
                >
                  Manage, Track and Sell{" "}
                </motion.span>{" "}
                Your Inventory Across Multiple Marketplaces
              </motion.h1>
              <motion.p
                className="mt-4 text-lg text-gray-700 dark:text-gray-300 sm:mt-6 sm:text-xl"
                initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
                animate={shouldReduceMotion ? {} : { opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                Streamline your sales process with seamless integration and
                real-time inventory tracking across{" "}
                <span className="font-semibold text-deepPurple dark:text-brightPurple">
                  10+ marketplaces
                </span>
                . Save time, increase sales, and grow your business
                effortlessly.
              </motion.p>
              <motion.div
                className="mt-10 flex flex-col space-y-4 md:space-y-0 md:flex-row items-center gap-x-6"
                initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
                animate={shouldReduceMotion ? {} : { opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                <Link to={"/signup"}>
                  <motion.button
                    onClick={undefined}
                    className="group relative flex items-center h-[56px] sm:h-[72px] justify-center gap-3 rounded-xl bg-gradient-to-r from-softBlue via-brightPurple to-deepPurple px-5 sm:px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-softBlue/30 hover:shadow-xl hover:shadow-softBlue/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-softBlue transition-all duration-300 overflow-hidden"
                    whileHover={{
                      scale: 1.05,
                      boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Animated gradient overlay */}
                    {!shouldReduceMotion && (
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                        style={{ willChange: "transform" }}
                        animate={shimmerAnimation}
                        transition={shimmerTransition}
                      />
                    )}
                    <motion.div
                      animate={iconAnimation}
                      transition={iconTransition}
                      className="relative z-10"
                    >
                      <RocketLaunchIcon
                        aria-hidden={true}
                        className="h-6 w-6 md:h-8 md:w-8"
                      />
                    </motion.div>
                    <div className="flex flex-col items-start my-0 space-y-0 relative z-10">
                      <span className="text-[11px] md:text-xs font-medium opacity-90">
                        Free Trial
                      </span>
                      <span className="text-base md:text-lg font-bold -mt-1 sm:mt-0">
                        Get Started
                      </span>
                    </div>
                    {/* Shine effect on hover */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100"
                      initial={{ x: "-100%" }}
                      whileHover={{ x: "100%" }}
                      transition={{ duration: 0.6 }}
                    />
                  </motion.button>
                </Link>
                <Link to={"#"}>
                  <motion.button
                    className="group relative flex items-center h-[56px] sm:h-[72px] justify-center gap-3 rounded-xl bg-gradient-to-br from-gray-600 via-gray-500 to-gray-600 px-5 sm:px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-gray-500/20 hover:shadow-xl hover:shadow-gray-500/30 cursor-not-allowed opacity-75"
                    disabled
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-700/50 rounded-xl to-transparent opacity-50" />
                    <ComputerDesktopIcon
                      aria-hidden={true}
                      className="h-6 w-6 md:h-8 md:w-8 relative z-10"
                    />
                    <div className="flex flex-col items-start my-0 space-y-0 relative z-10">
                      <span className="text-[10px] md:text-xs font-medium opacity-90">
                        Coming Soon
                      </span>
                      <span className="text-base md:text-lg font-bold">
                        Download for Desktop
                      </span>
                    </div>
                  </motion.button>
                </Link>
              </motion.div>
            </motion.div>
            <motion.div
              className="flex justify-center items-center w-full"
              initial={shouldReduceMotion ? false : { opacity: 0 }}
              animate={shouldReduceMotion ? {} : { opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.8 }}
            >
              <img
                src={platformConnectionsLight}
                alt="Platform connections diagram"
                className="w-[80%] sm:w-full max-w-3xl h-auto"
                loading="eager"
                decoding="async"
                width="1200"
                height="800"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(HeroSection);
